import React, { useMemo, useState } from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { Product } from '../../types/product';
import type { Season, UsageProfile } from '../../types/scan';
import { simulateDay } from '../../utils/energy';
interface DayProfileChartProps {
  usage: UsageProfile;
  product: Product;
}
const legend = [{
  label: 'Zonnestroom',
  color: '#F2B33D'
}, {
  label: 'Verbruik',
  color: '#33423B'
}, {
  label: 'Batterij (%)',
  color: '#A8CF33'
}];
export function DayProfileChart({
  usage,
  product
}: DayProfileChartProps) {
  const [season, setSeason] = useState<Season>('zomer');
  const data = useMemo(() => simulateDay(usage, product.capacity, product.power, season, 1), [usage, product, season]);
  return <section aria-labelledby="day-chart-title" className="rounded-3xl border border-line bg-white p-5 sm:p-6" data-id="element-396" data-mp="2nw4jpwicahs1">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-397" data-mp="2svza7yy7d5ck">
        <div data-id="element-398" data-mp="18pdaiauufwxk">
          <h2 id="day-chart-title" className="text-lg font-semibold text-ink" data-id="element-399" data-mp="18p3mutx9u1v1">Een dag met de {product.name}</h2>
          <p className="text-[14px] text-ink-muted" data-id="element-400" data-mp="13ph6wcq267ij">Opwek, verbruik en batterijstand per uur</p>
        </div>
        <div role="radiogroup" aria-label="Seizoen" className="flex rounded-full bg-canvas-deep p-1" data-id="element-401" data-mp="1hgncmqjxl1dv">
          {(['zomer', 'winter'] as Season[]).map(s => <button key={s} role="radio" aria-checked={season === s} onClick={() => setSeason(s)} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors duration-150 ${season === s ? 'bg-ink text-canvas' : 'text-ink-soft hover:text-ink'}`} data-id="element-402" data-mp="pqhgp0mwdkk9">
              {s}
            </button>)}
        </div>
      </div>

      <div className="mt-5 h-60" data-id="element-403" data-mp="2a8fg4nezcn13">
        <ResponsiveContainer width="100%" height="100%" data-id="element-404" data-mp="258awwpmote76">
          <ComposedChart data={data} margin={{
          top: 8,
          right: 0,
          bottom: 0,
          left: -18
        }} data-id="element-405" data-mp="259vqod16gt3z">
            <CartesianGrid vertical={false} stroke="#ECE9DF" data-id="element-406" data-mp="12w7lpvkpm3io" />
            <XAxis dataKey="label" tick={{
            fontSize: 12,
            fill: '#66736C'
          }} interval={5} tickLine={false} axisLine={false} data-id="element-407" data-mp="17s50vyk8l3tv" />
            <YAxis yAxisId="kw" tick={{
            fontSize: 12,
            fill: '#66736C'
          }} tickLine={false} axisLine={false} unit=" kW" width={62} data-id="element-408" data-mp="1bvkx4tse9261" />
            <YAxis yAxisId="soc" orientation="right" domain={[0, 100]} hide data-id="element-409" data-mp="k3ly1yjyhj96" />
            <Tooltip contentStyle={{
            borderRadius: 12,
            border: '1px solid #DFDBCD',
            fontSize: 13
          }} formatter={(value, name) => name === 'soc' ? [`${value}%`, 'Batterij'] : [`${Number(value).toFixed(2).replace('.', ',')} kW`, name === 'solar' ? 'Zon' : 'Verbruik']} data-id="element-410" data-mp="k1ixikl6g7q7" />
            <Area yAxisId="kw" type="monotone" dataKey="solar" stroke="#F2B33D" strokeWidth={2} fill="#F2B33D" fillOpacity={0.25} isAnimationActive={false} data-id="element-411" data-mp="f3dju9wau0t" />
            <Area yAxisId="kw" type="monotone" dataKey="load" stroke="#33423B" strokeWidth={2} fill="transparent" isAnimationActive={false} data-id="element-412" data-mp="3u14972g7z3jr" />
            <Line yAxisId="soc" type="monotone" dataKey="soc" stroke="#A8CF33" strokeWidth={2.5} dot={false} isAnimationActive={false} data-id="element-413" data-mp="3p213rgwbuvb9" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-ink-muted" data-id="element-414" data-mp="3g09w46i88eer">
        {legend.map(l => <li key={l.label} className="inline-flex items-center gap-2" data-id="element-415" data-mp="3b3tbhma8ki6f">
            <span className="h-2.5 w-2.5 rounded-full" style={{
          backgroundColor: l.color
        }} aria-hidden="true" data-id="element-416" data-mp="369fb15gpvkuw" />
            {l.label}
          </li>)}
      </ul>
    </section>;
}